import {
  agentDetailSchema,
  agentRegistrySchema,
  apiErrorSchema,
  analyticsDashboardSchema,
  businessRuleDetailSchema,
  businessRuleSummarySchema,
  conversationFlowSchema,
  flowSimulationSchema,
  flowValidationSchema,
  guardrailDetailSchema,
  intentWordingSchema,
  knowledgeAnalysisSchema,
  knowledgeBaseSchema,
  paginatedGuardrailsSchema,
  phraseTestResultSchema,
  type AgentDetail,
  type AgentRegistry,
  type AnalyticsDashboard,
  type BusinessRuleDetail,
  type BusinessRuleSummary,
  type ConversationFlow,
  type CreateReviewFlagInput,
  type FlowSimulation,
  type FlowValidation,
  type GuardrailDetail,
  type GovernanceActionInput,
  type IntentWording,
  type KnowledgeAnalysis,
  type KnowledgeBase,
  type PaginatedGuardrails,
  type PhraseTestResult,
  type SimulateFlowInput,
  type TestPhraseInput,
  type UpdateBusinessRuleInput,
  type UpdateConversationFlowInput,
  type UpdateGuardrailInput,
  type UpdateIntentWordingInput,
} from '@eairlines/contracts';
import { accessToken, expireSession } from '../auth/session';

const baseUrl = import.meta.env.VITE_API_URL ?? '/api';

export class ApiClientError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly correlationId?: string,
  ) {
    super(message);
  }
}

export async function request(path: string, init?: RequestInit): Promise<unknown> {
  const token = accessToken();
  const writing = !['GET', 'HEAD'].includes((init?.method ?? 'GET').toUpperCase());
  if (writing && import.meta.env.VITE_READ_ONLY_PREVIEW === 'true') throw new ApiClientError('Changes are unavailable in read-only preview.', 403);
  if (!token && (writing || /^\/agents\/[^/]+\/configuration(?:\/|$)/.test(path))) throw new ApiClientError('Sign-in is required.', 401);
  const headers = new Headers(init?.headers);
  headers.set('content-type', 'application/json');
  headers.delete('authorization');
  if (token) headers.set('authorization', `Bearer ${token}`);
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers,
    credentials: 'omit',
    redirect: 'error',
  });
  if (response.status === 401 && token) expireSession();
  const body: unknown = await response.json();
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(body);
    throw new ApiClientError(
      parsed.success ? parsed.data.message : 'The request could not be completed',
      response.status,
      parsed.success ? parsed.data.correlationId : undefined,
    );
  }
  return body;
}

export async function listGuardrails(): Promise<PaginatedGuardrails> {
  return paginatedGuardrailsSchema.parse(await request('/guardrails'));
}

export async function getGuardrail(id: string): Promise<GuardrailDetail> {
  return guardrailDetailSchema.parse(await request(`/guardrails/${id}`));
}

export async function updateGuardrail(
  id: string,
  input: UpdateGuardrailInput,
): Promise<GuardrailDetail> {
  return guardrailDetailSchema.parse(
    await request(`/guardrails/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  );
}

export async function runGuardrailTests(
  id: string,
  input: GovernanceActionInput,
): Promise<GuardrailDetail> {
  return guardrailDetailSchema.parse(
    await request(`/guardrails/${id}/tests`, { method: 'POST', body: JSON.stringify(input) }),
  );
}

export async function submitGuardrailForApproval(
  id: string,
  input: GovernanceActionInput,
): Promise<GuardrailDetail> {
  return guardrailDetailSchema.parse(
    await request(`/guardrails/${id}/submit-approval`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export async function getKnowledgeBase(): Promise<KnowledgeBase> {
  return knowledgeBaseSchema.parse(await request('/knowledge-base'));
}

export async function analyzeKnowledge(): Promise<KnowledgeAnalysis> {
  return knowledgeAnalysisSchema.parse(
    await request('/knowledge-base/analyze', { method: 'POST' }),
  );
}

export async function flagKnowledgePassage(
  passageId: string,
  input: CreateReviewFlagInput,
): Promise<void> {
  await request(`/knowledge-base/passages/${passageId}/review-flags`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function listBusinessRules(): Promise<BusinessRuleSummary[]> {
  return businessRuleSummarySchema.array().parse(await request('/business-rules'));
}

export async function getBusinessRule(id: string): Promise<BusinessRuleDetail> {
  return businessRuleDetailSchema.parse(await request(`/business-rules/${id}`));
}

export async function updateBusinessRule(
  id: string,
  input: UpdateBusinessRuleInput,
): Promise<BusinessRuleDetail> {
  return businessRuleDetailSchema.parse(
    await request(`/business-rules/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  );
}

export async function submitBusinessRuleForApproval(
  id: string,
  input: GovernanceActionInput,
): Promise<BusinessRuleDetail> {
  return businessRuleDetailSchema.parse(
    await request(`/business-rules/${id}/submit-approval`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export async function getConversationFlow(id: string): Promise<ConversationFlow> {
  return conversationFlowSchema.parse(await request(`/conversation-flows/${id}`));
}

export async function updateConversationFlow(
  id: string,
  input: UpdateConversationFlowInput,
): Promise<ConversationFlow> {
  return conversationFlowSchema.parse(
    await request(`/conversation-flows/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  );
}

export async function validateConversationFlow(id: string): Promise<FlowValidation> {
  return flowValidationSchema.parse(
    await request(`/conversation-flows/${id}/validate`, { method: 'POST' }),
  );
}

export async function simulateConversationFlow(
  id: string,
  input: SimulateFlowInput,
): Promise<FlowSimulation> {
  return flowSimulationSchema.parse(
    await request(`/conversation-flows/${id}/simulate`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export async function submitConversationFlowForApproval(
  id: string,
  input: GovernanceActionInput,
): Promise<ConversationFlow> {
  return conversationFlowSchema.parse(
    await request(`/conversation-flows/${id}/submit-approval`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export async function getIntentWording(id: string): Promise<IntentWording> {
  return intentWordingSchema.parse(await request(`/intent-wording/${id}`));
}

export async function updateIntentWording(
  id: string,
  input: UpdateIntentWordingInput,
): Promise<IntentWording> {
  return intentWordingSchema.parse(
    await request(`/intent-wording/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  );
}

export async function testIntentPhrase(
  id: string,
  input: TestPhraseInput,
): Promise<PhraseTestResult> {
  return phraseTestResultSchema.parse(
    await request(`/intent-wording/${id}/test`, { method: 'POST', body: JSON.stringify(input) }),
  );
}

export async function submitIntentWordingForApproval(
  id: string,
  input: GovernanceActionInput,
): Promise<IntentWording> {
  return intentWordingSchema.parse(
    await request(`/intent-wording/${id}/submit-approval`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export async function getAnalytics(from: string, to: string): Promise<AnalyticsDashboard> {
  return analyticsDashboardSchema.parse(
    await request(`/analytics?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  );
}

export async function getAgentRegistry(): Promise<AgentRegistry> {
  return agentRegistrySchema.parse(await request('/agents'));
}

export async function getAgent(key: string): Promise<AgentDetail> {
  return agentDetailSchema.parse(await request(`/agents/${encodeURIComponent(key)}`));
}
